import { login, sendToKafka } from '@/api/apiCommon';
import { NO_LABEL, YES_LABEL } from '@/core/formOptions';
import { nationalityOptions } from '@/core/formOptions.js';
import { covers, eiqMappingTranslations } from "@/core/textConstants";
import { ExtraFilters, InsuranceCovers } from '@/enums/filters';
import { useGtm } from '@gtm-support/vue-gtm';
import MurmurHash from 'imurmurhash';
import { format } from 'v-money3';
import { reactive, toRaw } from 'vue';

const loggingEnabled = import.meta.env.VITE_LOGGING === 'enabled';

const trackGTMEvent = (eventObject) => {
  // example object
  // {
  //   event: 'event name',
  //   category: 'category',
  //   action: 'click',
  //   label: 'My custom component trigger',
  //   value: 5000,
  //   noninteraction: false,
  // }
  const gtm = useGtm();

  gtm.trackEvent(eventObject);
};

// Varify custom-event goals
const trackVarifyUniqueEvent = (eventName) => {
  window.varifyTracker?.trackUniqueEvent?.(eventName);

  // Flag read by Varify audience-targeting scripts (e.g. "clicked details_button")
  try {
    sessionStorage.setItem(`varify_event_${eventName}`, '1');
  } catch (e) { /* storage unavailable */ }
};

const pushToDataLayer = (object) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(object);
}

const deepClone = (obj) => {
  let rawObj = toRaw(obj);

  if (rawObj === null || typeof rawObj !== 'object') {
    return rawObj;
  }

  const clone = Array.isArray(rawObj) ? [] : {};

  for (const key in rawObj) {
    if (Object.prototype.hasOwnProperty.call(rawObj, key)) {
      clone[key] = deepClone(rawObj[key]);
    }
  }

  return reactive(clone);
};

const addStyleToCoverText = (guid) => {
  let coverText = covers[guid];
  if (guid === InsuranceCovers.NaturalPhenomena) {
    coverText += "<span style=\"color: #029540;margin-left: 0.2rem;font-weight: bold;\">Nέο</span>"
  }
  return coverText;
}

// Replacer function to modify the serialization process
const replacer = (key, value) => {
  if (typeof value === 'object' && value !== null && 'id' in value && 'label' in value) {
    return value.id;
  }
  // Return the original value
  return value;
}

const jsonToFormData = (json, formData = new FormData(), parentKey = '') => {
  for (let key in json) {
    if (json.hasOwnProperty(key)) {
      let newKey = parentKey ? `${parentKey}[${key}]` : key;
      if (typeof json[key] === 'object' && json[key] !== null) {
        jsonToFormData(json[key], formData, newKey);
      } else {
        formData.append(newKey, json[key]);
      }
    }
  }
  return formData;
}

const mapToLegacyForm = (data) => jsonToFormData((JSON.parse(JSON.stringify(data, replacer))))

function isEmptyObject(obj) {
  return obj == null || (typeof obj === 'object' && Object.values(obj).every(isEmptyObject));
}

const shuffle = (array) => {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex != 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
};


const presentBoolean = (value) => {
  return value ? YES_LABEL : NO_LABEL;
}

const presentDate = (value) => {
  return new Date(value).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const presentDateOnlyMonth = (value) => {
  return new Date(value).toLocaleString('el-GR', { month: '2-digit', year: 'numeric' });
}

const presentCurrency = (value) => {
  return format(value, moneyOptionsNoDecimal);
}


const presentVehicleType = (value) => {
  return eiqMappingTranslations[value] ?? '';
}

const presentOwner = (value) => {
  switch (value) {
    case 0:
      return 'Ιδιώτης';
    case 1:
      return 'Ιδιώτης';
    case 2:
      return 'Ιδιώτης';
    case 3:
      return 'Εταιρεία';
    case 4:
      return 'Ιδιώτης';
    default:
      return '';
  }
}


const getDifferentFilters = (a, b) => {
  const differentFilters = [];
  for (const key in a) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      differentFilters.push(key);
    }
  }
  return differentFilters;
};

const isEqual = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

// const deepClone = (obj) => {
//   if (obj === null || typeof obj !== 'object') {
//     return obj;
//   }

//   const clone = Array.isArray(obj) ? [] : {};

//   for (const key in obj) {
//     if (Object.prototype.hasOwnProperty.call(obj, key)) {
//       clone[key] = deepClone(obj[key]);
//     }
//   }

//   return clone;
// };

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const guidToExtraFilter = (guid) => {
  if (guid === InsuranceCovers.NaturalPhenomenaBasic) {
    return ExtraFilters.BasicNaturalPhenomena;
  } else if (guid === InsuranceCovers.NaturalPhenomenaExtra) {
    return ExtraFilters.ExtraNaturalPhenomena;
  } else if (guid === InsuranceCovers.NaturalPhenomenaPremium) {
    return ExtraFilters.PremiumNaturalPhenomena;
  }
};



const moneyOptions = {
  prefix: '',
  suffix: '€',
  decimal: ',',
  thousands: '.',
  precision: 2,
  masked: false,
};

const moneyOptionsNoDecimal = {
  prefix: '',
  suffix: '€',
  decimal: ',',
  thousands: '.',
  precision: 0,
  masked: false,
  modelModifiers: { number: true }
};

const isNumber = (char) => {
  return /^\d$/.test(char);
};

const isCharLetter = (char) => {
  return char.toLowerCase() !== char.toUpperCase();
};

const isCharLetterOrNumber = (char) => {
  return isCharLetter(char) || isNumber(char);
};

const filterUnsupportedGreekCharacters = (character) => {
  const unsupportedGreekCharacters = [
    'Γ',
    'Δ',
    'Θ',
    'Λ',
    'Ξ',
    'Π',
    'Φ',
    'Ω',
    'Σ',
    'Ψ',
  ];

  if (unsupportedGreekCharacters.includes(character)) {
    return false;
  } else {
    return true;
  }
};

const translateLatinToGreek = (character) => {
  const mapping = {
    A: 'Α',
    B: 'Β',
    C: 'Ψ',
    D: 'Δ',
    E: 'Ε',
    F: 'Φ',
    G: 'Γ',
    H: 'Η',
    I: 'Ι',
    J: 'Ξ',
    K: 'Κ',
    L: 'Λ',
    M: 'Μ',
    N: 'Ν',
    O: 'Ο',
    P: 'Π',
    Q: 'Θ',
    R: 'Ρ',
    S: 'Σ',
    T: 'Τ',
    U: 'Θ',
    V: 'Ω',
    W: 'Σ',
    X: 'Χ',
    Y: 'Υ',
    Z: 'Ζ',
  };

  if (mapping[character]) {
    return mapping[character];
  } else {
    return character;
  }
};

const transformLicensePlate = (licensePlate) => {
  if (!licensePlate) {
    return '';
  }

  if (licensePlate.length === 7 && /^[A-Za-zΑ-Ωα-ω]{2}\d{4}.$/.test(licensePlate))
    return licensePlate.slice(0, -1);

  if (licensePlate.length > 7)
    return licensePlate.slice(0, 7);

  // Make uppercase
  // Remove everything that is not a letter or number
  // Replace latin characters with greek
  // Remove unsupported greek characters
  let transformedLicensePlate = licensePlate
    .toUpperCase()
    .split('')
    .filter(isCharLetterOrNumber)
    .map(translateLatinToGreek)
    .filter(filterUnsupportedGreekCharacters)
    .join('');

  // Get last character
  const lastChar = transformedLicensePlate.slice(-1);

  // Allow only letters for the first 2 or 3 characters
  if (transformedLicensePlate.length <= 2) {
    if (!isCharLetter(lastChar)) {
      return transformedLicensePlate.slice(0, -1)
    }
  }

  const lettersPart = transformedLicensePlate.slice(0, 3);
  const numbersPart = transformedLicensePlate.slice(3);

  // Allow letters or transition to numbers at the 3rd character
  if (transformedLicensePlate.length === 3) {
    const firstTwoChars = transformedLicensePlate.slice(0, 2);
    if (!isCharLetter(lastChar) && (!isNumber(lastChar) || !isCharLetter(firstTwoChars[0]) || !isCharLetter(firstTwoChars[1]))) {
      return transformedLicensePlate.slice(0, -1)
    }
  }

  // Allow only numbers after the first 2 or 3 characters
  if (transformedLicensePlate.length > 3) {
    if (!isNumber(lastChar)) {
      return transformedLicensePlate.slice(0, -1);
    }
  }

  // Ensure the correct format of 2-3 letters and 4 numbers
  if (transformedLicensePlate.length === 7) {
    if (lettersPart.length === 2 && numbersPart.length > 4) {
      return transformedLicensePlate.slice(0, -1);
    }
  }

  return transformedLicensePlate
}

const formatWithLeadingZero = (value) => {
  if (value === undefined || value === null || value === '') {
    return;
  }

  return value.toString().padStart(2, '0');
}

// Validate month (1-12)
const isValidMonth = (month) => {
  return month >= 1 && month <= 12;
}

// Validate year, customize range as needed
const isValidYear = (year) => {
  return year >= 1900 && year <= new Date().getFullYear();
}

const focusNextInput = (currentValue, nextInputRef, maxLength) => {
  if (currentValue.length === maxLength) {
    nextInputRef.value.focus();
  }
}

const hashString = (inputString) => {
  return MurmurHash(inputString).result().toString();
}

const logEvent = async (message, eventType, payload) => {
  if (loggingEnabled) {
    try {
      let token = await login()
      await sendToKafka(token, {
        topic: "eiq-insurance-web-app",
        key: hashString(message),
        eventLog: { message, eventType, payload }
      })
    } catch (error) {
    }
  }
}

const transformPacketTypeToLiteral = (packetType) => {
  const packetTypeMap = {
    0: "BASIC",
    1: "EXTRA",
    2: "PREMIUM"
  };

  return packetTypeMap[packetType];
}

const transformPacketTypeToId = (packetType) => {
  const packetTypeMap = {
    "BASIC": 0,
    "EXTRA": 1,
    "PREMIUM": 2
  };

  return packetTypeMap[packetType];
}

const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDifference = today.getMonth() - birth.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

const mapFuel = (engineType) => {
  const mapEngineTypeToId = {
    "Βενζινοκινητήρας": 1,
    "Πετρελαιοκινητήρας": 2,
    "Υβριδικός Βενζίνης/Ηλεκτρικός": 6,
    "Ηλεκτροκινητήρας": 3,
    "Υβριδικός Πετρελαίου/Ηλεκτρικός": 6,
    "Βάνκελ": 1
  }

  return mapEngineTypeToId[engineType];
}

const mapRoofType = (roofType) => {
  if (roofType.length === 0) {
    return 0;
  }

  const mapRoofTypeToId = {
    "Υφασμάτινη": 2,
  }

  return mapRoofTypeToId[roofType];
}

const instantFilterHasCover = (packageItem, coverTypeGuid) => {
  return packageItem.covers.some((cover) =>  {
    if (cover.guid == InsuranceCovers.NaturalPhenomena && cover.subfeatures.length > 0) {
      return cover.subfeatures?.some(subCover => subCover.type.guid == coverTypeGuid)
    }
    return false
  })
}

const instantFilterPackageHasFriendlySettlementCover = (packageItem, coverTypeGuid) => {
  return packageItem.covers.some((cover) =>  {
    return (cover.data.type.guid === coverTypeGuid)
  })
}

const isEmptyValue = (value) => {
    return value === undefined || value == null || value === '';
}

const parseMoneyValue = (value) => {
  if (isEmptyValue(value)) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  const lastDot = raw.lastIndexOf('.');
  const lastComma = raw.lastIndexOf(',');
  const lastSeparator = Math.max(lastDot, lastComma);
  const stripNonDigits = (input) => input.replace(/[^\d-]/g, '');

  if (lastSeparator === -1) {
    const numeric = Number(stripNonDigits(raw));
    return Number.isNaN(numeric) ? null : numeric;
  }

  const integerPart = stripNonDigits(raw.slice(0, lastSeparator));
  const decimalPart = raw.slice(lastSeparator + 1).replace(/[^\d]/g, '');

  if (!decimalPart || decimalPart.length > 2) {
    const numeric = Number(stripNonDigits(raw));
    return Number.isNaN(numeric) ? null : numeric;
  }

  const normalized = `${integerPart || '0'}.${decimalPart}`;
  const numeric = Number(normalized);
  return Number.isNaN(numeric) ? null : numeric;
};

const emptyToUndefined = (value, originalValue) =>
  isEmptyValue(originalValue) ? undefined : value;

const escapeHtml = (value) => {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const parseNationalityValue = (nationality) => {
  const DEFAULT_NATIONALITY_CODE = 67;

  if (!nationality) {
    return DEFAULT_NATIONALITY_CODE;
  }

  const matched = nationalityOptions.find(option => {
    const value = option?.value ?? option?.label;
    return value === nationality;
  });

  return matched?.code ?? DEFAULT_NATIONALITY_CODE;
};

export {
  addStyleToCoverText, calculateAge, deepClone, emptyToUndefined, escapeHtml, filterUnsupportedGreekCharacters, focusNextInput, formatWithLeadingZero, getDifferentFilters, guidToExtraFilter, instantFilterHasCover, instantFilterPackageHasFriendlySettlementCover, isCharLetter,
  isCharLetterOrNumber, isEmptyObject, isEmptyValue, isEqual, isNumber, isValidMonth, isValidYear, jsonToFormData, logEvent, mapFuel, mapRoofType, mapToLegacyForm, moneyOptions, moneyOptionsNoDecimal, parseMoneyValue, parseNationalityValue, presentBoolean, presentCurrency, presentDate,
  presentDateOnlyMonth, presentOwner, presentVehicleType, pushToDataLayer, replacer, shuffle, sleep, trackGTMEvent, trackVarifyUniqueEvent, transformLicensePlate, transformPacketTypeToId, transformPacketTypeToLiteral, translateLatinToGreek
};

